import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { partsApi } from '@/api/parts';
import { suppliersApi } from '@/api/suppliers';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select } from '@/components/Form';
import { useToast } from '@/components/Toast';

// docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §5.2 — LOT 입고 등록.
// ADMIN / MANAGER / MFG_MANAGER 만. UI 진입은 PartLotsPage 헤더에서.

export interface PartLotCreateModalProps {
  open: boolean;
  onClose: () => void;
}

export function PartLotCreateModal({ open, onClose }: PartLotCreateModalProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const [partCatalogId, setPartCatalogId] = useState('');
  const [lotCode, setLotCode] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [manufacturedAt, setManufacturedAt] = useState('');
  const [receivedQty, setReceivedQty] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  const catalogQ = useQuery({
    queryKey: ['parts', 'catalog', { active: true }],
    queryFn: () => partsApi.listCatalog({ active: true }),
    enabled: open,
    staleTime: 60_000,
  });

  // v0.5.0 — supplier picker.
  const suppliersQ = useQuery({
    queryKey: ['suppliers', { active: true }],
    queryFn: () => suppliersApi.list({ active: true }),
    enabled: open,
    staleTime: 60_000,
  });

  const createMx = useMutation({
    mutationFn: () =>
      partsApi.createLot({
        partCatalogId,
        lotCode: lotCode.trim(),
        // v0.5.0 — supplierId 우선, supplierName 은 fallback (legacy).
        supplierId: supplierId || undefined,
        supplierName: supplierId ? undefined : supplierName.trim() || undefined,
        manufacturedAt: manufacturedAt || undefined,
        receivedQty: typeof receivedQty === 'number' ? receivedQty : 0,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['part-lots'] });
      qc.invalidateQueries({ queryKey: ['parts', 'lots'] });
      toast.success('LOT를 입고 등록했습니다.');
      handleClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function handleClose(): void {
    if (createMx.isPending) return;
    setPartCatalogId('');
    setLotCode('');
    setSupplierId('');
    setSupplierName('');
    setManufacturedAt('');
    setReceivedQty('');
    setError(null);
    onClose();
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    if (!partCatalogId) return setError('카탈로그를 선택해 주세요.');
    if (!lotCode.trim()) return setError('LOT 코드를 입력해 주세요.');
    if (typeof receivedQty !== 'number' || receivedQty < 1)
      return setError('입고 수량은 1 이상이어야 합니다.');
    createMx.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="LOT 입고 등록"
      width={520}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createMx.isPending}
          >
            취소
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={createMx.isPending}
            data-testid="lot-create-submit"
          >
            {createMx.isPending ? '저장 중…' : '입고 등록'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {error && <FormError>{error}</FormError>}

        <Field label="부품 카탈로그" htmlFor="catalog" required>
          <Select
            id="catalog"
            value={partCatalogId}
            onChange={(e) => setPartCatalogId(e.target.value)}
            disabled={catalogQ.isLoading}
            data-testid="lot-catalog-select"
          >
            <option value="">— 선택 —</option>
            {(catalogQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                [{c.category}] {c.displayName} ({c.sku})
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="LOT 코드"
          htmlFor="lot-code"
          required
          hint="공급사 LOT 번호나 사내 코드입니다. 예: LOT-2026-Q2-0042"
        >
          <Input
            id="lot-code"
            value={lotCode}
            onChange={(e) => setLotCode(e.target.value)}
            placeholder="LOT-2026-Q2-0042"
            data-testid="lot-code-input"
          />
        </Field>

        <Field
          label="공급사 (선택)"
          htmlFor="supplier"
          // 종전 문구는 '먼저 공급사 메뉴에서 등록 권장' 이었다. 오너 결정
          // 2026-08-28 로 공급사 메뉴가 숨겨져 **없는 메뉴를 가리키는 안내**가
          // 되었으므로, 실제로 지금 할 수 있는 일(아래 free-text 칸)을 가리킨다.
          // 화면에서 사라진 길을 문구가 계속 안내하면 사용자는 자기가 못 찾는
          // 것이라 여기고 헤맨다.
          hint={
            (suppliersQ.data?.length ?? 0) > 0
              ? '목록에 없는 공급사는 아래 칸에 직접 입력해 주세요.'
              : '등록된 공급사가 없습니다. 아래 칸에 직접 입력해 주세요.'
          }
        >
          <Select
            id="supplier"
            value={supplierId}
            onChange={(e) => {
              setSupplierId(e.target.value);
              setSupplierName(''); // always clear on supplierId change — mutually exclusive
            }}
            disabled={suppliersQ.isLoading}
            data-testid="lot-supplier-select"
          >
            <option value="">— 선택 안 함 —</option>
            {(suppliersQ.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} · {s.displayName}
              </option>
            ))}
          </Select>
        </Field>

        {!supplierId && (
          <Field
            label="공급사 직접 입력"
            htmlFor="supplier-name"
            hint="가능하면 위 목록에서 공급사를 골라 주세요. 목록에 없을 때만 씁니다."
          >
            <Input
              id="supplier-name"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              placeholder="예: ABC Manufacturing"
            />
          </Field>
        )}

        <Field
          label="제조일 (선택)"
          htmlFor="manufactured-at"
          hint="공급사가 알려 준 제조일입니다."
        >
          <Input
            id="manufactured-at"
            type="date"
            value={manufacturedAt}
            onChange={(e) => setManufacturedAt(e.target.value)}
          />
        </Field>

        <Field label="입고 수량" htmlFor="received-qty" required>
          <Input
            id="received-qty"
            type="number"
            min={1}
            value={receivedQty}
            onChange={(e) => {
              const v = e.target.value;
              setReceivedQty(v === '' ? '' : Number.parseInt(v, 10));
            }}
            placeholder="10"
            data-testid="lot-qty-input"
          />
        </Field>
      </form>
    </Modal>
  );
}
